import posthog from 'posthog-js';

// PostHog Configuration - API key set via GitHub secrets, baked in at build time
const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

// Initialize PostHog
export const initAnalytics = () => {
  if (POSTHOG_API_KEY && typeof window !== 'undefined') {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
    });
  }
};

// Identify user after login
export const identifyUser = (userId: string, email: string, name?: string) => {
  if (POSTHOG_API_KEY) {
    posthog.identify(userId, {
      email,
      name: name || email.split('@')[0],
    });
  }
};

// Reset user on logout
export const resetUser = () => {
  if (POSTHOG_API_KEY) {
    posthog.reset();
  }
};

// Track custom events
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (POSTHOG_API_KEY) {
    posthog.capture(eventName, properties);
  }
};

// Pre-defined event trackers
export const analytics = {
  // Page views (automatic, but can be called manually)
  pageView: (pageName: string) => {
    trackEvent('$pageview', { page: pageName });
  },

  // Login/Logout
  login: (email: string, method: 'google' | 'email') => {
    trackEvent('user_login', { email, method });
  },

  logout: () => {
    trackEvent('user_logout');
  },

  // Dashboard
  viewDashboard: (viewMode: 'weekly' | 'quarterly') => {
    trackEvent('view_dashboard', { view_mode: viewMode });
  },

  filterDashboard: (filters: { startDate?: string; endDate?: string; sortBy?: string }) => {
    trackEvent('filter_dashboard', filters);
  },

  // Expenses
  viewExpenses: (viewMode: 'weekly' | 'quarterly') => {
    trackEvent('view_expenses', { view_mode: viewMode });
  },

  viewLocationExpenses: (location: string) => {
    trackEvent('view_location_expenses', { location });
  },

  // Comparison
  startComparison: (locationA: string, locationB: string) => {
    trackEvent('start_comparison', { location_a: locationA, location_b: locationB });
  },

  alignItems: (enabled: boolean) => {
    trackEvent('toggle_align_items', { enabled });
  },

  // PDF Download
  downloadPdf: (type: 'comparison' | 'report', locations?: string[]) => {
    trackEvent('download_pdf', { type, locations });
  },

  // Critical Items
  toggleCriticalItems: (showCritical: boolean) => {
    trackEvent('toggle_critical_items', { show_critical: showCritical });
  },

  // Feedback
  submitFeedback: () => {
    trackEvent('submit_feedback');
  },

  // Tour
  startTour: () => {
    trackEvent('start_tour');
  },

  completeTour: () => {
    trackEvent('complete_tour');
  },

  skipTour: () => {
    trackEvent('skip_tour');
  },
};

export default posthog;
